import React, { useState, useEffect, useRef } from 'react';
import { MdSend, MdSmartToy, MdPerson, MdCheckCircle, MdRefresh, MdRocketLaunch, MdLightbulb, MdSearch, MdTune } from 'react-icons/md';
import './ConversationalWorkoutGenerator.css';

const TIERS = {
  BASE: 'base',
  ENRICHMENT: 'enrichment',
  CUSTOM: 'custom',
};

const ConversationalWorkoutGenerator = ({ clients = [], onGenerate, onCancel, isGeneratingExternal = false, isTemplateMode = false }) => {
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTier, setCurrentTier] = useState(TIERS.BASE);
  const [formData, setFormData] = useState({
    clientId: '',
    goal: '',
    duration: 4,
    frequency: 3,
    level: 'Intermedio',
    session_time: '45-60',
    split_preference: 'AI Decision',
    progression_type: 'Standard',
    limitations: '',
    focus_areas: '',
    exclusions: '',
    notes: '',
    isTemplate: isTemplateMode,
    target_audience: '',
    target_age_range: '',
    target_weight_range: '',
  });
  const [isTyping, setIsTyping] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [tierComplete, setTierComplete] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasInitialized = useRef(false);

  const baseSteps = isTemplateMode ? [
    {
      id: 'target_audience',
      question: "Per che tipo di clientela vuoi creare questo template?",
      type: 'select',
      field: 'target_audience',
      options: [
        { value: 'Principianti', label: 'Principianti (prime esperienze in palestra)' },
        { value: 'Sportivi', label: 'Sportivi (esperienza intermedia-avanzata)' },
        { value: 'Over 50', label: 'Over 50 (focus salute e mobilità)' },
        { value: 'Dimagrimento generico', label: 'Dimagrimento generico' },
        { value: 'Atleti funzionali', label: 'Atleti funzionali (CrossFit-style)' },
      ],
      allowCustom: true,
      placeholder: 'Oppure descrivi il target...',
    },
    {
      id: 'goal',
      question: "Qual è l'obiettivo principale di questo template?",
      type: 'select',
      field: 'goal',
      options: [
        { value: 'Dimagrimento', label: 'Dimagrimento' },
        { value: 'Ipertrofia', label: 'Ipertrofia (Massa muscolare)' },
        { value: 'Ricomposizione corporea', label: 'Ricomposizione corporea' },
        { value: 'Mantenimento', label: 'Mantenimento' },
        { value: 'Forza', label: 'Forza' },
        { value: 'Condizionamento', label: 'Condizionamento atletico' },
      ],
      allowCustom: true,
      placeholder: 'Oppure descrivi un obiettivo personalizzato...',
    },
    {
      id: 'level',
      question: "Per quale livello di esperienza è pensato?",
      type: 'select',
      field: 'level',
      options: [
        { value: 'Principiante', label: 'Principiante' },
        { value: 'Intermedio', label: 'Intermedio' },
        { value: 'Avanzato', label: 'Avanzato' },
      ],
    },
    {
      id: 'frequency',
      question: "Quante volte a settimana prevede l'allenamento?",
      type: 'number',
      field: 'frequency',
      min: 1, max: 7, default: 3,
    },
    {
      id: 'duration',
      question: "Per quante settimane è pensato il ciclo?",
      type: 'number',
      field: 'duration',
      min: 1, max: 12, default: 4,
    },
    {
      id: 'time',
      question: "Durata prevista per ogni sessione?",
      type: 'select',
      field: 'session_time',
      options: [
        { value: '30-45', label: '30-45 minuti (Veloce)' },
        { value: '45-60', label: '45-60 minuti (Standard)' },
        { value: '60-90', label: '60-90 minuti (Completo)' },
        { value: '90+', label: 'Oltre 90 minuti (Volume Alto)' },
      ],
    },
  ] : [
    {
      id: 'welcome',
      question: "Per quale cliente vuoi creare la scheda?",
      type: 'client',
      field: 'clientId',
      options: clients.map(c => ({ value: c.id, label: `${c.nome} ${c.cognome}` })),
    },
    {
      id: 'goal',
      question: "Qual è l'obiettivo principale?",
      type: 'select',
      field: 'goal',
      options: [
        { value: 'Dimagrimento', label: 'Dimagrimento' },
        { value: 'Ipertrofia', label: 'Ipertrofia (Massa muscolare)' },
        { value: 'Ricomposizione corporea', label: 'Ricomposizione corporea' },
        { value: 'Mantenimento', label: 'Mantenimento' },
        { value: 'Forza', label: 'Forza' },
        { value: 'Condizionamento', label: 'Condizionamento atletico' },
      ],
      allowCustom: true,
      placeholder: 'Oppure descrivi un obiettivo personalizzato...',
    },
    {
      id: 'level',
      question: "Qual è il livello di esperienza del cliente?",
      type: 'select',
      field: 'level',
      options: [
        { value: 'Principiante', label: 'Principiante' },
        { value: 'Intermedio', label: 'Intermedio' },
        { value: 'Avanzato', label: 'Avanzato' },
      ],
    },
    {
      id: 'frequency',
      question: "Quante volte a settimana può allenarsi?",
      type: 'number',
      field: 'frequency',
      min: 1, max: 7, default: 3,
    },
    {
      id: 'duration',
      question: "Per quante settimane vuoi programmare la scheda?",
      type: 'number',
      field: 'duration',
      min: 1, max: 12, default: 4,
    },
    {
      id: 'time',
      question: "Quanto tempo ha a disposizione per ogni sessione?",
      type: 'select',
      field: 'session_time',
      options: [
        { value: '30-45', label: '30-45 minuti (Veloce)' },
        { value: '45-60', label: '45-60 minuti (Standard)' },
        { value: '60-90', label: '60-90 minuti (Completo)' },
        { value: '90+', label: 'Oltre 90 minuti (Volume Alto)' },
      ],
    },
  ];

  const enrichmentSteps = [
    {
      id: 'split',
      question: "Preferenza di Split Routine?",
      type: 'select',
      field: 'split_preference',
      options: [
        { value: 'Full Body', label: 'Full Body' },
        { value: 'Upper/Lower', label: 'Upper / Lower' },
        { value: 'Push/Pull/Legs', label: 'Push / Pull / Legs' },
        { value: 'Body Part Split', label: 'Bro Split (Monofrequenza)' },
        { value: 'AI Decision', label: 'Lascia decidere all\'AI' },
      ],
    },
    {
      id: 'progression',
      question: "Tipo di progressione nel ciclo?",
      type: 'select',
      field: 'progression_type',
      options: [
        { value: 'Standard', label: 'Scheda unica per tutto il periodo' },
        { value: 'Linear', label: 'Progressione carichi settimanale' },
        { value: 'Biweekly', label: 'Cambio stimolo ogni 2 settimane' },
        { value: 'Monthly', label: 'Cambio stimolo ogni 4 settimane' },
      ],
    },
    {
      id: 'limitations',
      question: "Ci sono limitazioni fisiche o infortuni da considerare? (scrivi 'no' se nessuna)",
      type: 'textarea',
      field: 'limitations',
      optional: true,
    },
  ];

  const customSteps = [
    {
      id: 'focus_areas',
      question: "Focus specifici? Es: 'Enfatizzare dorsali', 'Più esercizi compound', 'Lavoro su punti deboli gambe'... (scrivi 'no' se nessuno)",
      type: 'textarea',
      field: 'focus_areas',
      optional: true,
    },
    {
      id: 'exclusions',
      question: "Esclusioni o preferenze? Es: 'No stacchi da terra', 'Preferisco macchine ai cavi liberi', 'Evita esercizi overhead'... (scrivi 'no' se nessuna)",
      type: 'textarea',
      field: 'exclusions',
      optional: true,
    },
  ];

  const getAllSteps = () => {
    if (currentTier === TIERS.BASE) return baseSteps;
    if (currentTier === TIERS.ENRICHMENT) return [...baseSteps, ...enrichmentSteps];
    return [...baseSteps, ...enrichmentSteps, ...customSteps];
  };

  const totalPossibleSteps = baseSteps.length + enrichmentSteps.length + customSteps.length;

  useEffect(() => {
    if (!hasInitialized.current && messages.length === 0) {
      hasInitialized.current = true;
      const greeting = isTemplateMode
        ? "Creiamo un template! Per quale tipo di clientela è pensato?"
        : "Ciao! Iniziamo a creare la scheda. Per quale cliente?";
      addBotMessage(greeting);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { scrollToBottom(); }, [messages.length, isTyping]);

  useEffect(() => {
    if (inputRef.current && !tierComplete && !isComplete) inputRef.current.focus();
  }, [currentStep, tierComplete, isComplete]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addBotMessage = (text, delay = 400) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', text, timestamp: new Date() }]);
      setIsTyping(false);
    }, delay);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text, timestamp: new Date() }]);
  };

  const getCurrentStepInTier = () => {
    const steps = getAllSteps();
    if (currentStep >= steps.length) return null;
    return steps[currentStep];
  };

  const processAnswer = (value, displayText) => {
    const steps = getAllSteps();
    const step = steps[currentStep];

    let cleanValue = value;
    if (step.optional && typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      if (lower === 'no' || lower === 'nessuna' || lower === 'niente' || lower === 'nessuno') {
        cleanValue = '';
      }
    }

    setFormData(prev => ({ ...prev, [step.field]: cleanValue }));
    addUserMessage(displayText || value);

    const isLastInTier =
      (currentTier === TIERS.BASE && currentStep >= baseSteps.length - 1) ||
      (currentTier === TIERS.ENRICHMENT && currentStep >= baseSteps.length + enrichmentSteps.length - 1) ||
      (currentTier === TIERS.CUSTOM && currentStep >= steps.length - 1);

    if (isLastInTier) {
      setTimeout(() => {
        if (currentTier === TIERS.CUSTOM) {
          setIsComplete(true);
          addBotMessage("Perfetto! Ho tutte le informazioni. Vuoi generare la scheda?");
        } else {
          setTierComplete(true);
          const tierLabel = currentTier === TIERS.BASE ? 'base' : 'di dettaglio';
          addBotMessage(`Informazioni ${tierLabel} completate! Puoi generare ora oppure aggiungere più dettagli per una scheda ancora più precisa.`);
        }
        setUserInput('');
      }, 600);
    } else {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        addBotMessage(steps[currentStep + 1].question);
        setUserInput('');
      }, 600);
    }
  };

  const handleQuickSelect = (value, label) => {
    setUserInput(label);
    processAnswer(value, label);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const step = getCurrentStepInTier();
    if (!step) return;
    let value = userInput.trim();

    if (step.type === 'client') {
      const match = step.options.find(opt => opt.label.toLowerCase() === value.toLowerCase());
      if (!match) {
        addBotMessage("Seleziona un cliente dalla lista.");
        return;
      }
      processAnswer(match.value, match.label);
      return;
    }

    if (step.type === 'select') {
      const match = step.options.find(opt =>
        opt.label.toLowerCase() === value.toLowerCase() || opt.value.toLowerCase() === value.toLowerCase()
      );
      if (match) {
        processAnswer(match.value, match.label);
        return;
      }
      if (step.allowCustom) {
        processAnswer(value, userInput);
        return;
      }
      addBotMessage(`Seleziona una delle opzioni valide: ${step.options.map(o => o.label).join(', ')}`);
      return;
    }

    if (step.type === 'number') {
      const num = parseInt(value);
      if (isNaN(num) || num < step.min || num > step.max) {
        addBotMessage(`Inserisci un numero tra ${step.min} e ${step.max}.`);
        return;
      }
      processAnswer(num, String(num));
      return;
    }

    processAnswer(value, userInput);
  };

  const handleContinueToNextTier = () => {
    setTierComplete(false);
    if (currentTier === TIERS.BASE) {
      setCurrentTier(TIERS.ENRICHMENT);
      setTimeout(() => {
        addBotMessage(enrichmentSteps[0].question);
      }, 300);
    } else if (currentTier === TIERS.ENRICHMENT) {
      setCurrentTier(TIERS.CUSTOM);
      setTimeout(() => {
        addBotMessage(customSteps[0].question);
      }, 300);
    }
  };

  const handleGenerateNow = () => {
    setTierComplete(false);
    setIsComplete(true);
    addBotMessage("Perfetto! Vuoi generare la scheda?");
  };

  const handleGenerate = () => {
    if (isGenerating || !onGenerate) return;
    setIsGenerating(true);
    addBotMessage("Sto generando la scheda personalizzata. Questo potrebbe richiedere qualche secondo...", 200);
    onGenerate(formData);
  };

  useEffect(() => {
    if (isGeneratingExternal) {
      setIsGenerating(true);
    } else if (isGeneratingExternal === false && isGenerating) {
      setTimeout(() => setIsGenerating(false), 500);
    }
  }, [isGeneratingExternal, isGenerating]);

  const handleReset = () => {
    setMessages([]);
    setCurrentStep(0);
    setCurrentTier(TIERS.BASE);
    setTierComplete(false);
    setIsComplete(false);
    setIsGenerating(false);
    setUserInput('');
    setClientSearchQuery('');
    setFormData({
      clientId: '', goal: '', duration: 4, frequency: 3, level: 'Intermedio',
      session_time: '45-60', split_preference: 'AI Decision', progression_type: 'Standard',
      limitations: '', focus_areas: '', exclusions: '', notes: '',
      isTemplate: isTemplateMode, target_audience: '', target_age_range: '', target_weight_range: '',
    });
    hasInitialized.current = false;
    setTimeout(() => {
      hasInitialized.current = true;
      addBotMessage(isTemplateMode
        ? "Ricominciamo! Per quale tipo di clientela è pensato?"
        : "Ricominciamo! Per quale cliente?");
    }, 300);
  };

  const currentStepData = getCurrentStepInTier();

  const getFilteredClients = () => {
    if (!currentStepData || currentStepData.type !== 'client') return [];
    if (!clientSearchQuery.trim()) return currentStepData.options;
    const q = clientSearchQuery.toLowerCase().trim();
    return currentStepData.options.filter(opt => opt.label.toLowerCase().includes(q));
  };
  const filteredClients = getFilteredClients();

  const completedSteps = currentStep + (isComplete ? 1 : (tierComplete ? 1 : 0));
  const progress = (completedSteps / totalPossibleSteps) * 100;

  const tierLabel = currentTier === TIERS.BASE ? 'Base' : currentTier === TIERS.ENRICHMENT ? 'Dettaglio' : 'Personalizzazione';

  const buildSummary = () => {
    const items = [];
    if (!isTemplateMode && formData.clientId) {
      const c = clients.find(cl => cl.id === parseInt(formData.clientId));
      if (c) items.push({ label: 'Cliente', value: `${c.nome} ${c.cognome}` });
    }
    if (isTemplateMode && formData.target_audience) {
      items.push({ label: 'Target', value: formData.target_audience });
    }
    if (formData.goal) items.push({ label: 'Obiettivo', value: formData.goal });
    if (formData.level) items.push({ label: 'Livello', value: formData.level });
    if (formData.frequency) items.push({ label: 'Frequenza', value: `${formData.frequency} giorni/sett.` });
    if (formData.duration) items.push({ label: 'Durata', value: `${formData.duration} settimane` });
    if (formData.session_time) items.push({ label: 'Sessione', value: `${formData.session_time} min` });
    if (formData.split_preference && formData.split_preference !== 'AI Decision') {
      items.push({ label: 'Split', value: formData.split_preference });
    }
    if (formData.progression_type && formData.progression_type !== 'Standard') {
      items.push({ label: 'Progressione', value: formData.progression_type });
    }
    if (formData.limitations) items.push({ label: 'Limitazioni', value: formData.limitations });
    if (formData.focus_areas) items.push({ label: 'Focus', value: formData.focus_areas });
    if (formData.exclusions) items.push({ label: 'Esclusioni', value: formData.exclusions });
    return items;
  };

  return (
    <div className="conversational-generator">
      <div className="cg-header">
        <div className="cg-header-content">
          <div className="cg-title">
            <MdSmartToy className="cg-icon" />
            <span>{isTemplateMode ? 'AI Template Builder' : 'Assistente AI'}</span>
            <span style={{ fontSize: '11px', opacity: 0.6, marginLeft: '8px' }}>Fase: {tierLabel}</span>
          </div>
          <div className="cg-progress-container">
            <div className="cg-progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="cg-progress-text">{Math.round(progress)}% completato</div>
        </div>
        {(isComplete || tierComplete) && (
          <button className="cg-reset-btn" onClick={handleReset} title="Ricomincia">
            <MdRefresh />
          </button>
        )}
      </div>

      <div className="cg-chat-container">
        <div className="cg-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`cg-message cg-message-${msg.type}`}>
              <div className="cg-message-avatar">
                {msg.type === 'bot' ? <MdSmartToy /> : <MdPerson />}
              </div>
              <div className="cg-message-content">
                <div className="cg-message-bubble">{msg.text}</div>
                <div className="cg-message-time">
                  {msg.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="cg-message cg-message-bot">
              <div className="cg-message-avatar"><MdSmartToy /></div>
              <div className="cg-message-content">
                <div className="cg-message-bubble cg-typing"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}

          {/* Quick options for current step */}
          {!isComplete && !tierComplete && currentStepData && (
            <div className="cg-input-suggestions">
              {currentStepData.type === 'select' && currentStepData.options && (
                <>
                  <div className="cg-quick-options">
                    {currentStepData.options.map((opt, idx) => (
                      <button key={idx} className="cg-quick-option" onClick={() => handleQuickSelect(opt.value, opt.label)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {currentStepData.allowCustom && (
                    <div className="cg-custom-hint">
                      <span><MdLightbulb className="cg-hint-icon" /> Oppure scrivi un valore personalizzato</span>
                    </div>
                  )}
                </>
              )}

              {currentStepData.type === 'client' && (
                <>
                  <div className="cg-client-search">
                    <MdSearch className="cg-search-icon" />
                    <input
                      type="text" className="cg-search-input"
                      placeholder="Cerca cliente..."
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      autoFocus
                    />
                    {clientSearchQuery && (
                      <button className="cg-clear-search" onClick={() => setClientSearchQuery('')}>&#10005;</button>
                    )}
                  </div>
                  {filteredClients.length > 0 ? (
                    <div className="cg-quick-options cg-client-list">
                      {filteredClients.map((opt, idx) => (
                        <button key={idx} className="cg-quick-option cg-client-option"
                          onClick={() => { handleQuickSelect(opt.value, opt.label); setClientSearchQuery(''); }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="cg-no-results">
                      {clientSearchQuery ? `Nessun cliente trovato per "${clientSearchQuery}"` : 'Nessun cliente disponibile.'}
                    </div>
                  )}
                </>
              )}

              {currentStepData.type === 'number' && (
                <div className="cg-number-hint">
                  <span>Inserisci un numero tra {currentStepData.min} e {currentStepData.max}</span>
                </div>
              )}
            </div>
          )}

          {/* Tier completion: generate now or add more details */}
          {tierComplete && !isComplete && (
            <div className="cg-tier-actions" style={{
              display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px',
              background: 'rgba(235, 205, 97, 0.05)', borderRadius: '12px', margin: '8px 0'
            }}>
              <button className="cg-action-btn cg-action-primary" onClick={handleGenerateNow}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <MdRocketLaunch /> Genera Ora
              </button>
              {currentTier !== TIERS.CUSTOM && (
                <button className="cg-action-btn cg-action-secondary" onClick={handleContinueToNextTier}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <MdTune /> {currentTier === TIERS.BASE ? 'Aggiungi Dettagli (+3 domande)' : 'Personalizza Ancora (+2 domande)'}
                </button>
              )}
            </div>
          )}

          {/* Summary when complete */}
          {isComplete && (
            <div className="cg-summary">
              <div className="cg-summary-title">
                <MdCheckCircle className="cg-check-icon" />
                <span>Riepilogo</span>
              </div>
              <div className="cg-summary-content">
                {buildSummary().map((item, idx) => (
                  <div key={idx} className="cg-summary-item">
                    <strong>{item.label}:</strong> {item.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Text input for non-client, non-complete steps */}
        {!isComplete && !tierComplete && currentStepData && currentStepData.type !== 'client' && (
          <form className="cg-input-form" onSubmit={handleSubmit}>
            {currentStepData.type === 'textarea' ? (
              <textarea ref={inputRef} className="cg-input" value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={currentStepData.placeholder || 'Scrivi qui...'} rows="3"
                required={!currentStepData.optional}
              />
            ) : (
              <input ref={inputRef} type={currentStepData.type === 'number' ? 'number' : 'text'}
                className="cg-input" value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={currentStepData.placeholder || 'Scrivi qui...'}
                min={currentStepData.min} max={currentStepData.max} required
              />
            )}
            <button type="submit" className="cg-send-btn" disabled={!userInput.trim()}>
              <MdSend />
            </button>
          </form>
        )}

        {/* Client selection hint */}
        {!isComplete && !tierComplete && currentStepData && currentStepData.type === 'client' && (
          <div className="cg-client-selection-hint">
            <p><MdSearch className="cg-hint-icon" /> Seleziona un cliente dalla lista qui sopra.</p>
          </div>
        )}

        {/* Generate / Reset actions */}
        {isComplete && (
          <div className="cg-actions">
            {!isGenerating ? (
              <>
                <button className="cg-action-btn cg-action-secondary" onClick={handleReset}>
                  <MdRefresh /> Ricomincia
                </button>
                <button className="cg-action-btn cg-action-primary" onClick={handleGenerate}>
                  <MdRocketLaunch /> Genera Scheda
                </button>
                {onCancel && (
                  <button className="cg-action-btn cg-action-cancel" onClick={onCancel}>Annulla</button>
                )}
              </>
            ) : (
              <div className="cg-generating">
                <div className="cg-generating-spinner"></div>
                <span className="cg-generating-text">Generazione in corso...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationalWorkoutGenerator;
